import { useState, useEffect, useRef } from 'react';
import Paywall from './Paywall';
import Payment from './Payment';
import Confirmation from './Confirmation';
import Billing from '../Settings/Billing';
import LogOutButton from 'Components/LogOutButton';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { MainAppReducer } from 'Types';
import { isActiveSubscription } from 'Utils/Subscription';
import { onEntitlementRequired } from 'Utils/entitlementGate';
import axios from 'axios';
import { Box, Button } from '@mui/material';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
// import { useAuth } from "react-oidc-context";

const PaymentFlow = () => {
  // const data = useSelector((state: MainAppReducer) => state.mainAppReducer)
  // const { user } = useAuth();
  const { user: _user, currentAuthToken } = useAuthWrapperContext();

  const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
  const { userData } = useSelector((state: MainAppReducer) => state.mainAppReducer);

  const [showPaywall, setShowPaywall] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [planTypeSelected, setPlanTypeSelected] = useState('');
  const [discountCodeValid, setDiscountCodeValid] = useState<boolean | null>(null);
  const [discountCode, setDiscountCode] = useState('');
  const [showDiscountCode, setShowDiscountCode] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean | null>(null);
  const [billingError, setBillingError] = useState(false);
  const [billingRevision, setBillingRevision] = useState(0);
  const wasPaymentGateShowing = useRef(false);

  const _location = useLocation();
  // If paywall; not allowed to exit.
  const paywallExitAllowed = false; //location.pathname !== '/lesson'

  useEffect(() => {
    let canceled = false;
    setBillingError(false);
    setHasPaymentMethod(null);
    if (
      userData?.subscription_status === 'past_due' &&
      ['apple', 'google'].includes(userData.subscription_provider || '')
    ) {
      setHasPaymentMethod(true);
    } else if (userData?.subscription_status === 'past_due') {
      (async () => {
        try {
          const token = await currentAuthToken();
          const { data } = await axios.get<{
            details: { isCustomer?: boolean; hasPaymentMethodId?: boolean };
          }>(`${baseUrl}stripe/user-billing-details`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000,
          });
          if (!canceled)
            setHasPaymentMethod(!!data.details?.isCustomer && !!data.details?.hasPaymentMethodId);
        } catch {
          // An unavailable billing service is not evidence that it is safe to buy again.
          if (!canceled) setBillingError(true);
        }
      })();
    }
    return () => {
      canceled = true;
    };
  }, [
    userData?.subscription_status,
    userData?.subscription_provider,
    userData?.user_id,
    currentAuthToken,
    baseUrl,
    billingRevision,
  ]);

  useEffect(() => {
    if (userData?.coupon_code) {
      // If they have coupon code in userData; that means that the code was valid.
      // WE will need to ensure they are grandfathered in if we decide to rotate coupon codes (possibly query and update all users with a certain code and update them so that when the trial expires they will have discount!)
      setDiscountCode(userData?.coupon_code);
      setShowDiscountCode(true);
      setDiscountCodeValid(true);
    }
    const subscriptionStatus = userData?.subscription_status;
    if (showPaywall || showPayment) {
      wasPaymentGateShowing.current = true;
    }

    if (!subscriptionStatus) {
      setShowPaywall(false);
      setShowPayment(false);
      wasPaymentGateShowing.current = false;
      return;
    }

    if (subscriptionStatus === 'past_due' && hasPaymentMethod == null) {
      return;
    }

    if (isActiveSubscription(subscriptionStatus, hasPaymentMethod ?? false)) {
      const shouldShowConfirmation = wasPaymentGateShowing.current;
      setShowPaywall(false);
      setShowPayment(false);
      if (shouldShowConfirmation) {
        setShowConfirmation(true);
        wasPaymentGateShowing.current = false;
      }
      return;
    }

    if (
      ['unpaid', 'incomplete', 'paused'].includes(subscriptionStatus) ||
      (subscriptionStatus === 'past_due' && hasPaymentMethod === false)
    ) {
      setShowPaywall(false);
      setShowPayment(false);
      return;
    }

    if (!showConfirmation) {
      setShowPayment(false);
      setShowPaywall(true);
    }
  }, [userData, hasPaymentMethod, showPaywall, showPayment, showConfirmation]);

  const needsRecovery =
    ['unpaid', 'incomplete', 'paused'].includes(userData?.subscription_status || '') ||
    (userData?.subscription_status === 'past_due' && hasPaymentMethod === false);
  // MUS-532: when the server's Layer-2 entitlement gate denies a content fetch
  // (403), the shared axios layer fires this signal. Force the paywall so a
  // load race (fetch beats the paywall render) shows the paywall rather than an
  // error/blank/crash. The subscription-status reconciliation effect above still
  // governs the entitled case — an entitled-looking user is not trapped here
  // (that client/server split-brain is MUS-533's product decision), so this only
  // makes the paywall appear promptly for a genuinely-lapsed user.
  useEffect(() => {
    const unsubscribe = onEntitlementRequired(() => {
      setShowPaywall(true);
    });
    return unsubscribe;
  }, []);

  return (
    <>
      {billingError && userData?.subscription_status === 'past_due' && (
        <>
          <Box className="modal-outer" />
          <Box className="modal-inner2" role="alert">
            <p className="paywall-desc-text">
              We couldn’t confirm your existing subscription. Please retry before making another
              purchase.
            </p>
            <Button onClick={() => setBillingRevision((value) => value + 1)}>Retry billing</Button>
            <LogOutButton />
          </Box>
        </>
      )}
      {needsRecovery && (
        <>
          <Box className="modal-outer" />
          <Box
            className="modal-inner2"
            role="dialog"
            aria-modal="true"
            aria-label="Resolve your subscription"
            sx={{ justifyContent: 'flex-start', overflowY: 'auto', gap: 1 }}
          >
            <h3 className="paywall-desc-title">Resolve your current subscription</h3>
            <p className="paywall-desc-text">
              Review your current billing terms and update your payment details to continue.
            </p>
            <Billing />
            <LogOutButton />
          </Box>
        </>
      )}
      {showPaywall &&
        !needsRecovery &&
        !(userData?.subscription_status === 'past_due' && hasPaymentMethod === null) && (
          <Paywall
            setShowPaywall={setShowPaywall}
            setShowPayment={setShowPayment}
            setPlanTypeSelected={setPlanTypeSelected}
            exitAllowed={paywallExitAllowed}
            discountCodeValid={discountCodeValid}
            setDiscountCodeValid={setDiscountCodeValid}
            discountCode={discountCode}
            setDiscountCode={setDiscountCode}
            showDiscountCode={showDiscountCode}
            setShowDiscountCode={setShowDiscountCode}
          />
        )}
      {showPayment && (
        <Payment
          setShowPaywall={setShowPaywall}
          setShowPayment={setShowPayment}
          setShowConfirmation={setShowConfirmation}
          planTypeSelected={planTypeSelected}
          exitAllowed={false}
          discountCode={discountCode}
          discountCodeValid={discountCodeValid}
          setDiscountCodeValid={setDiscountCodeValid}
        />
      )}
      {showConfirmation && <Confirmation setShowConfirmation={setShowConfirmation} />}
    </>
  );
};
export default PaymentFlow;
